<template>
  <Modal ref="modal" class="send-approval-modal">
    <template v-if="!created">
      <h2 class="box__title">{{ $t('createChecklist.title') }}</h2>
      <p class="send-approval__intro">{{ $t('createChecklist.intro') }}</p>

      <FormGroup :label="$t('createChecklist.listTitle')" required>
        <input
          v-model="title"
          class="input"
          :placeholder="$t('createChecklist.listTitlePlaceholder')"
          maxlength="255"
        />
      </FormGroup>

      <FormGroup :label="$t('createChecklist.message')">
        <textarea
          v-model="message"
          class="input send-approval__textarea"
          rows="2"
          :placeholder="$t('createChecklist.messagePlaceholder')"
          maxlength="10000"
        ></textarea>
      </FormGroup>

      <FormGroup :label="$t('createChecklist.items')" required>
        <div class="checklist-editor">
          <div
            v-for="(item, index) in items"
            :key="index"
            class="checklist-editor__row"
          >
            <input
              v-model="item.label"
              class="input checklist-editor__label"
              :placeholder="$t('createChecklist.itemPlaceholder')"
              maxlength="255"
              @keydown.enter.prevent="addItem(index)"
            />
            <Dropdown
              v-model="item.kind"
              :show-search="false"
              class="checklist-editor__kind"
            >
              <DropdownItem
                :name="$t('checklistPage.kind.file')"
                value="file"
              ></DropdownItem>
              <DropdownItem
                :name="$t('checklistPage.kind.text')"
                value="text"
              ></DropdownItem>
              <DropdownItem
                :name="$t('checklistPage.kind.confirm')"
                value="confirm"
              ></DropdownItem>
            </Dropdown>
            <a
              class="checklist-editor__required"
              :class="{ 'checklist-editor__required--on': item.required }"
              :title="$t('createChecklist.required')"
              @click="item.required = !item.required"
              >*</a
            >
            <a class="checklist-editor__remove" @click="removeItem(index)"
              ><i class="iconoir-cancel"></i
            ></a>
          </div>
          <a class="send-approval__upload" @click="addItem()">
            <i class="iconoir-plus"></i> {{ $t('createChecklist.addItem') }}
          </a>
        </div>
        <div class="checklist-editor__presets">
          <span>{{ $t('createChecklist.presets') }}</span>
          <a
            v-for="preset in presets"
            :key="preset.key"
            class="send-approval__recent-chip"
            @click="applyPreset(preset)"
            >{{ preset.label }}</a
          >
        </div>
      </FormGroup>

      <FormGroup :label="$t('sendApproval.contact')">
        <div v-if="recentContacts.length > 0" class="send-approval__recent">
          <a
            v-for="(contact, index) in recentContacts"
            :key="index"
            class="send-approval__recent-chip"
            @click="applyContact(contact)"
          >
            {{ contact.name || contact.email || contact.phone }}
          </a>
        </div>
        <div class="send-approval__contact-grid">
          <input
            v-model="contactName"
            class="input"
            :placeholder="$t('sendApproval.contactName')"
            maxlength="255"
          />
          <input
            v-model="contactPhone"
            class="input"
            :placeholder="$t('sendApproval.contactPhone')"
            maxlength="64"
          />
          <input
            v-model="contactEmail"
            class="input"
            :placeholder="$t('sendApproval.contactEmail')"
            maxlength="255"
          />
        </div>
      </FormGroup>

      <div class="send-approval__options">
        <Checkbox v-model="remindersEnabled">
          {{ $t('createChecklist.reminders') }}
        </Checkbox>
      </div>

      <div class="actions actions--right">
        <Button
          type="primary"
          size="large"
          :loading="saving"
          :disabled="!title.trim() || validItems.length === 0 || saving"
          @click="submit"
        >
          {{ $t('createChecklist.create') }}
        </Button>
      </div>
    </template>

    <template v-else>
      <h2 class="box__title">{{ $t('sendApproval.readyTitle') }}</h2>
      <p class="send-approval__ready-text">
        {{ $t('createChecklist.readyText') }}
      </p>
      <div class="send-approval__link">
        <input
          class="input"
          :value="created.public_url"
          readonly
          @focus="$event.target.select()"
        />
        <Button type="secondary" icon="iconoir-copy" @click="copyLink">
          {{ $t('sendApproval.copy') }}
        </Button>
      </div>
      <div class="send-approval__channels">
        <Button type="primary" icon="iconoir-chat-bubble" @click="openWhatsApp">
          WhatsApp
        </Button>
        <Button type="secondary" @click="hide()">
          {{ $t('sendApproval.done') }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import { notifyIf } from '@baserow/modules/core/utils/error'
import ApprovalsService from '@baserow/modules/core/services/approvals'
import { copyToClipboard } from '@baserow/modules/database/utils/clipboard'
import { buildChecklistShareMessage } from '@baserow/modules/database/utils/approvalShare'

const newItem = () => ({ label: '', kind: 'file', required: true })

export default {
  name: 'CreateChecklistModal',
  mixins: [modal],
  props: {
    row: { type: Object, required: true },
    table: { type: Object, required: true },
    fields: { type: Array, required: true },
  },
  emits: ['created'],
  data() {
    return {
      title: '',
      message: '',
      items: [newItem()],
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      remindersEnabled: true,
      recentContacts: [],
      saving: false,
      created: null,
    }
  },
  computed: {
    service() {
      return ApprovalsService(this.$client)
    },
    validItems() {
      return this.items.filter((i) => i.label.trim())
    },
    presets() {
      return [
        {
          key: 'loja',
          label: this.$t('createChecklist.presetStore'),
          items: [
            { label: 'Contrato social / CNPJ (PDF)', kind: 'file' },
            { label: 'Acesso ao painel da loja (convite)', kind: 'text' },
            { label: 'Acesso ao Google Analytics / Meta Ads', kind: 'text' },
            { label: 'Logo em alta resolução', kind: 'file' },
            { label: 'Fotos dos produtos', kind: 'file', required: false },
          ],
        },
        {
          key: 'prefeitura',
          label: this.$t('createChecklist.presetCity'),
          items: [
            { label: 'Ofício / portaria de autorização (PDF)', kind: 'file' },
            { label: 'Planilha de dados do período', kind: 'file' },
            { label: 'Acesso ao sistema (usuário e perfil)', kind: 'text' },
            { label: 'Contato do responsável técnico', kind: 'text' },
            { label: 'Confirmar cronograma combinado', kind: 'confirm' },
          ],
        },
      ]
    },
  },
  methods: {
    show(...args) {
      this.reset()
      this.fetchContacts()
      return modal.methods.show.call(this, ...args)
    },
    reset() {
      const primary = this.fields.find((f) => f.primary)
      const name = primary ? String(this.row[`field_${primary.id}`] || '') : ''
      this.title = name ? `Pendências · ${name}` : ''
      this.message = ''
      this.items = [newItem()]
      this.contactName = ''
      this.contactPhone = ''
      this.contactEmail = ''
      this.remindersEnabled = true
      this.saving = false
      this.created = null
    },
    async fetchContacts() {
      try {
        const { data } = await this.service.contacts(this.table.id)
        this.recentContacts = data.contacts
      } catch (e) {
        this.recentContacts = []
      }
    },
    applyContact(contact) {
      this.contactName = contact.name || this.contactName
      this.contactPhone = contact.phone || this.contactPhone
      this.contactEmail = contact.email || this.contactEmail
    },
    addItem(afterIndex = null) {
      const item = newItem()
      if (afterIndex === null) this.items.push(item)
      else this.items.splice(afterIndex + 1, 0, item)
    },
    removeItem(index) {
      this.items.splice(index, 1)
      if (this.items.length === 0) this.items.push(newItem())
    },
    applyPreset(preset) {
      const existing = this.validItems
      this.items = [
        ...existing,
        ...preset.items.map((i) => ({ required: true, ...i })),
      ]
    },
    async submit() {
      this.saving = true
      try {
        const { data } = await this.service.createChecklist(
          this.table.id,
          this.row.id,
          {
            title: this.title,
            message: this.message,
            items: this.validItems,
            contact_name: this.contactName,
            contact_phone: this.contactPhone,
            contact_email: this.contactEmail,
            reminders_enabled: this.remindersEnabled,
          }
        )
        this.created = data.checklist
        this.$emit('created', data.checklist)
      } catch (error) {
        notifyIf(error, 'createChecklist')
      }
      this.saving = false
    },
    copyLink() {
      copyToClipboard(this.created.public_url)
      this.$store.dispatch('toast/info', {
        title: this.$t('rowApprovals.copiedTitle'),
        message: this.$t('rowApprovals.copied'),
      })
    },
    openWhatsApp() {
      const message = buildChecklistShareMessage(this.created)
      const digits = (this.created.contact_phone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
  },
}
</script>
